import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  removeAccessAdminToken,
  removeAccessToken,
  removeAdminInfoFromLocalStorage,
  removeUserInfoFromLocalStorage,
  setAccessToken,
  setUserInfoFromLocalStorage,
} from "../../shared/utils/auth";
import authApi from "../api/LoginApi";
import {
  ActionResponse,
  FileUrlResponse,
  LoginDto,
  UserChangePasswordFromMailDTO,
  UserMemberDto,
} from "../../types/auth";
import { AlarmRequest, AlarmResponse } from "../../types/common";
import FileSaver from "file-saver";
import { DocDownloadRequestDTO } from "../../types/files";

interface LoginState {
  alarmResponse?: AlarmResponse;
  apiToken: string;
  loginInfo?: LoginDto;
  actionInfo?: ActionResponse;
  fileUrl?: FileUrlResponse;
}

interface LoginRequestDto {
  userId: string;
  userPwd: string;
  otp: string;
}

// # initial state
const initialState: LoginState = {
  apiToken: "",
  loginInfo: undefined,
  alarmResponse: undefined,
  actionInfo: undefined,
  fileUrl: undefined,
};

export const trunkSessionNew = createAsyncThunk("login/new", async () => {
  const apiToken = await authApi.sessionNew();
  return apiToken;
});
export const trunkSessionLogout = createAsyncThunk(
  "sessions/logout",
  async () => {
    const apiToken = await authApi.sessionLogout();
    return apiToken;
  }
);
export const loginSession = createAsyncThunk(
  "login/login",
  async ({ userId, userPwd, otp }: LoginRequestDto) => {
    const resp = await authApi.sessionLogin(userId, userPwd, otp);
    return resp;
  }
);
export const trunkGetLogInByUid = createAsyncThunk(
  "login/loginUid",
  async (uid: string) => {
    return await authApi.getLogInByUid(uid);
  }
);
export const sendOtpEmail = createAsyncThunk(
  "login/sendOtp",
  async (userId: string) => {
    const resp = await authApi.sendOTPEMail(userId);
    return resp;
  }
);
export const trunkCheckOtpEmail = createAsyncThunk(
  "login/sendOtp/check",
  async (mailOTP: string) => {
    const resp = await authApi.checkMailOtp(mailOTP);
    return resp;
  }
);

export const checkUserValidSignupUser = createAsyncThunk(
  "login/checkUser",
  async (userId: string) => {
    return await authApi.checkUserValidSignupUser(userId);
  }
);
export const trunkSendPasswordInit = createAsyncThunk(
  "login/resetPW",
  async (userId: string) => {
    return await authApi.sendPasswordInit(userId);
  }
);
export const trunkGetGoogleOTPInfo = createAsyncThunk(
  "login/googleOTP",
  async (userId: string) => {
    return await authApi.getGoogleOTPInfo(userId);
  }
);
export const trunkRegisterGoogleOTPInfo = createAsyncThunk(
  "login/googleOTP/register",
  async (userId: string) => {
    return await authApi.registerGoogleOTPInfo(userId);
  }
);
export const trunkStartEsetAuthenticate = createAsyncThunk(
  "login/eset/start",
  async (userId: string) => {
    return await authApi.startEsetAuthenticate(userId);
  }
);
export const trunkPollingEsetAuthenticate = createAsyncThunk(
  "login/eset/polling",
  async (req: { pushChallengeId: string; userId: string }) => {
    return await authApi.checkEsetAuthenticate(req.pushChallengeId, req.userId);
  }
);
export const trunkLoginGoogleOtp = createAsyncThunk(
  "login/googleOTP/login",
  async (req: { userId: string; otp: string }) => {
    return await authApi.loginGoogleOtp(req);
  }
);
export const trunkJoinMember = createAsyncThunk(
  "join/member",
  async ({ uuid, users }: { uuid: string; users: UserMemberDto[] }) => {
    return await authApi.joinMember(uuid, users);
  }
);
export const trunkGetAlarmList = createAsyncThunk(
  "alarm/myAlarmList",
  async ({ userId, order, logId }: AlarmRequest) => {
    return await authApi.getAlarmList({
      userId,
      order,
      logId,
    });
  }
);
export const trunkReadAlarm = createAsyncThunk(
  "alarm/readAlarm",
  async ({ userId, order, logId }: AlarmRequest) => {
    return await authApi.readAlarm({ logId: logId });
  }
);
export const trunkReadAllAlarm = createAsyncThunk(
  "alarm/readAllAlarm",
  async ({ userId, order, logId }: AlarmRequest) => {
    return await authApi.readAllAlarm();
  }
);
export const trunkGetInformationFromUid = createAsyncThunk(
  "actionUrl/info",
  async (uid: string) => {
    return await authApi.getInformationFromUid(uid);
  }
);
export const trunkGetFileUrlFromUid = createAsyncThunk(
  "actionUrl/fileUrl",
  async (req: { uid: string; token?: string }) => {
    return await authApi.getFileUrlFromUid(req);
  }
);
export const trunkGetFileUrlDownloadFromUid = createAsyncThunk(
  "actionUrl/fileDownloadUrl",
  async (req: { uid: string; token?: string }) => {
    return await authApi.getFileUrlDownloadFromUid(req);
  }
);
export const trunkGetFileSimpleInfo = createAsyncThunk(
  "actionUrl/file/simeple",
  async (syncId: string) => {
    return await authApi.getFileSimpleInfo(syncId);
  }
);
export const trunkChangePasswordFromMail = createAsyncThunk(
  "actionUrl/updatePW/fromMail",
  async ({
    req,
    initOtp,
  }: {
    req: UserChangePasswordFromMailDTO;
    initOtp: boolean;
  }) => {
    return await authApi.changePasswordFromMail(req, initOtp);
  }
);
export const trunkDocHtmlDownload = createAsyncThunk(
  "actionUrl/htmlDownload",
  async (req: {
    uid: string;
    syncId: string;
    revisionNo: string;
    startDownload?: any;
  }) => {
    return await authApi.docHtmlDownload(
      req.uid,
      req.syncId,
      req.revisionNo,
      req.startDownload
    );
  }
);
const loginSlice = createSlice({
  name: "login",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(loginSession.fulfilled, (state, { payload: data }) => {
      setAccessToken(data.apiToken);
      setUserInfoFromLocalStorage(data);
      removeAccessAdminToken();
      removeAdminInfoFromLocalStorage();
      state.loginInfo = data;
    });
    builder.addCase(
      trunkGetLogInByUid.fulfilled,
      (state, { payload: loginDto }) => {
        setAccessToken(loginDto.apiToken);
        setUserInfoFromLocalStorage(loginDto);
        removeAccessAdminToken();
        removeAdminInfoFromLocalStorage();
        state.loginInfo = loginDto;
      }
    );
    builder.addCase(trunkGetLogInByUid.rejected, (state, error) => {
      const agent = navigator.userAgent.toLowerCase();
      if (error.error.name === "NOT_FOUND_USER") {
        if (
          agent.includes("macintosh") &&
          !agent.includes("safari") &&
          (window as any)?.webkit?.messageHandlers
        ) {
          (window as any)?.webkit?.messageHandlers.doLogout.postMessage(
            JSON.stringify({ dsdCode: "", error: "NOT_FOUND_USER" })
          );
          return;
        } else if (
          window?.chrome?.webview &&
          window?.chrome?.webview?.hostObjects
        ) {
          window?.chrome?.webview?.hostObjects.client.doLogout(
            JSON.stringify({ dsdCode: "", error: "NOT_FOUND_USER" })
          );
          return;
        }
      }
    });
    builder.addCase(
      trunkCheckOtpEmail.fulfilled,
      (state, { payload: data }) => {
        setAccessToken(data.apiToken);
        setUserInfoFromLocalStorage(data);
        removeAccessAdminToken();
        removeAdminInfoFromLocalStorage();
        state.loginInfo = data;
      }
    );
    builder.addCase(
      trunkLoginGoogleOtp.fulfilled,
      (state, { payload: data }) => {
        setAccessToken(data.apiToken);
        setUserInfoFromLocalStorage(data);
        removeAccessAdminToken();
        removeAdminInfoFromLocalStorage();
        state.loginInfo = data;
      }
    );
    builder.addCase(
      trunkPollingEsetAuthenticate.fulfilled,
      (state, { payload: data }) => {
        if (!data.apiToken) return;
        setAccessToken(data.apiToken);
        setUserInfoFromLocalStorage(data);
        removeAccessAdminToken();
        removeAdminInfoFromLocalStorage();
        state.loginInfo = data;
      }
    );

    builder.addCase(trunkSessionLogout.fulfilled, (state, arg) => {
      removeAccessAdminToken();
      removeAdminInfoFromLocalStorage();
      removeAccessToken();
      removeUserInfoFromLocalStorage();
      state.loginInfo = undefined;
    });
    builder.addCase(trunkSessionLogout.rejected, () => {
      removeAccessAdminToken();
      removeAdminInfoFromLocalStorage();
      removeAccessToken();
      removeUserInfoFromLocalStorage();
    });
    builder.addCase(trunkGetAlarmList.fulfilled, (state, arg) => {
      const alarmResponse = arg.payload;
      if (
        state.alarmResponse === undefined ||
        state.alarmResponse.firstLogId < alarmResponse.firstLogId ||
        arg.meta.arg.logId === ""
      ) {
        state.alarmResponse = alarmResponse;
      } else {
        state.alarmResponse = {
          ...alarmResponse,
          alarmResults: [
            ...state.alarmResponse.alarmResults,
            ...(alarmResponse.alarmResults ?? []),
          ],
        };
      }
    });
    builder.addCase(
      trunkReadAlarm.fulfilled,
      (state, { payload: alarmResponse }) => {
        const beforeStatus = state.alarmResponse?.alarmResults.filter(
          (item) => item.logId === alarmResponse.logId
        )[0]?.alarmStatus;
        if (beforeStatus === "S" && state.alarmResponse) {
          state.alarmResponse = {
            ...state.alarmResponse,
            unreadCount: state.alarmResponse.unreadCount - 1,
            alarmResults: state.alarmResponse.alarmResults.map((item) =>
              item.logId === alarmResponse.logId
                ? { ...item, alarmStatus: "D" }
                : item
            ),
          };
        }
      }
    );
    builder.addCase(
      trunkReadAllAlarm.fulfilled,
      (state, { payload: alarmResponse }) => {
        if (state.alarmResponse)
          state.alarmResponse = {
            ...state.alarmResponse,
            unreadCount: 0,
            alarmResults: state.alarmResponse.alarmResults.map((item) => {
              return { ...item, alarmStatus: "D" };
            }),
          };
      }
    );
    builder.addCase(
      trunkGetInformationFromUid.fulfilled,
      (state, { payload: actionInfo }) => {
        state.actionInfo = actionInfo;
      }
    );
    builder.addCase(
      trunkGetFileUrlFromUid.fulfilled,
      (state, { payload: fileUrl }) => {
        state.fileUrl = fileUrl;
      }
    );
    builder.addCase(
      trunkGetFileUrlDownloadFromUid.fulfilled,
      (state, { payload: res }) => {
        const file = res.data;
        const header = res.headers;
        const regex =
          /filename[^;=\n]*=(?:(\\?['"])(.*?)\1|(?:[^\s]+'.*?')?([^;\n]*))/i;
        const contentDisposition =
          header["content-disposition"].match(regex)[0];
        let fileName = contentDisposition.split("filename=");
        fileName = decodeURIComponent(fileName[1]);
        const blob = new Blob([file], { type: "application/download" });
        FileSaver.saveAs(blob, fileName);
      }
    );
    builder.addCase(
      trunkDocHtmlDownload.fulfilled,
      (state, { payload: res }) => {
        if (res.data && res.headers) {
          const file = res.data;
          const header = res.headers;
          const regex =
            /filename[^;=\n]*=(?:(\\?['"])(.*?)\1|(?:[^\s]+'.*?')?([^;\n]*))/i;
          const contentDisposition =
            header["content-disposition"].match(regex)[0];
          let fileName = contentDisposition.split("filename=");
          fileName = decodeURIComponent(fileName[1]);
          const blob = new Blob([file], { type: "application/download" });
          FileSaver.saveAs(blob, fileName);
        }
      }
    );
    builder.addCase(
      trunkGetInformationFromUid.rejected,
      (state, { payload: actionInfo }) => {
        state.actionInfo = {
          uniqueId: "",
          targetType: "",
          targetValue: "",
          urlAction: "FAIL",
          authType: "",
          makerId: "",
          entryTime: 0,
          expiredTime: 0,
          syncId: "",
          revisionNo: 0,
          workgroupId: "",
        };
      }
    );
  },
});
export const authActions = loginSlice.actions;

export default loginSlice.reducer;
